import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from "../components/SEO";


/* ══════════════════════════════════════════════════════════
   PR Hospital Designers — Hospital Infrastructure in Khammam (city page)
══════════════════════════════════════════════════════════ */

const CITY = {
  name: 'Khammam',
  state: 'Telangana',
  hasOwnEC: false,
  nearestEC: {
    name: 'PR Infra & Hospitals Designers',
    city: 'Hyderabad',
    address: '8-1/7/7, beside GVR Villa\'s, Sundar Rao Nagar, Bahadurpally, Hyderabad, Telangana 500043',
    hours: 'Monday to Saturday | 10 AM – 7 PM',
    appt1: 'Site Consultation — 60 minutes',
    appt2: 'Design Walkthrough — 45 minutes',
    phone: '+91 9993999840',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=8-1/7/7%2C%20beside%20GVR%20Villa%27s%2C%20Sundar%20Rao%20Nagar%2C%20Bahadurpally%2C%20Hyderabad%2C%20Telangana%20500043',
  },
  localProject: {
    name: 'Haritha Mother & Child',
    location: 'Khammam',
    type: 'Mother & Child',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Haritha%20Mother%20%26%20Child%2C%20Khammam',
  },
};

const WHATSAPP_NUMBER = '919993999840';

const CITIES_LIST = [
  'Bhuvanagiri', 'Hyderabad', 'Indresham', 'Jangaon', 'Khammam',
  'Mahabubnagar', 'Narsapur', 'Rajahmundry', 'Sangareddy', 'Vijayawada',
];

const CITY_PATHS = {
  Bhuvanagiri: '/bhuvanagiri',
  Hyderabad: '/hyderabad',
  Indresham: '/indresham',
  Jangaon: '/jangaon',
  Khammam: '/khammam',
  Mahabubnagar: '/mahabubnagar',
  Narsapur: '/narsapur',
  Rajahmundry: '/rajahmundry',
  Sangareddy: '/sangareddy',
  Vijayawada: '/vijayawada',
};

const PROCESS_STEPS = [
  { icon: '📋', title: 'We assess', text: 'A site visit and clinical workflow review before a single drawing is made.' },
  { icon: '📐', title: 'We design', text: 'NABH-compliant layouts for OTs, ICUs and wards, refined with you in 3D.' },
  { icon: '🏗️', title: 'We build', text: 'Civil, MEP, medical gases, furniture and IT — delivered by one accountable team.' },
];

const ESTIMATORS = [
  { icon: '🏥', title: 'Full Hospital Build', text: 'Get an approximate costing for a greenfield hospital project.' },
  { icon: '🛏️', title: 'ICU Setup', text: 'Get an approximate costing for your ICU fit-out.' },
  { icon: '🩻', title: 'Diagnostic Centre', text: 'Get an approximate costing for a diagnostic room build-out.' },
];

const REVIEWS = [
  { name: 'Dr. Suresh Reddy', initials: 'SR', text: 'PR Hospital Designers delivered our OT block fully NABH-compliant, on the timeline they committed to.' },
  { name: 'Anitha Krishnamurthy', initials: 'AK', text: 'The consultation was thorough and never felt rushed. Material options were extensive.' },
];

const WHY_US = [
  { icon: '📐', title: 'NABH-Ready Design', text: 'Layouts planned against NABH and infection-control standards from day one.' },
  { icon: '⚡', title: '45–90 Day Fit-outs', text: 'OT and ICU fit-outs delivered fast, without cutting corners.' },
  { icon: '🔩', title: 'Single-Vendor', text: 'Architecture, MEP, furniture and IT under one contract, one team.' },
  { icon: '🛡️', title: '10-Year Warranty', text: 'Structural and MEP work backed by an industry-leading warranty.' },
  { icon: '📞', title: 'Dedicated PM', text: 'One project manager, accountable from kickoff to handover.' },
  { icon: '💰', title: 'No Hidden Costs', text: 'Transparent, itemised quotations before work begins.' },
];

const INSPIRATION = [
  { icon: '🏥', title: 'OT Suite Designs', path: '/ot-suite-designs' },
  { icon: '🛏️', title: 'ICU Layout Designs', path: '/icu-layout-designs' },
  { icon: '🛌', title: 'Patient Ward Designs', path: '/patient-ward-designs' },
];

const FAQS = [
  { q: 'Can I meet a hospital infrastructure consultant near Khammam?', a: 'Our nearest Experience Centre to Khammam is in Hyderabad. Walk in to consult our clinical specialists, or request a site visit and we\'ll come to you.' },
  { q: 'Can I have a meeting at my site instead?', a: 'Yes — we regularly conduct on-site consultations. Share your details below and our team will schedule a visit.' },
  { q: 'What can I expect from a PR Hospital Designers consultation?', a: 'A review of your clinical workflow, space constraints and compliance goals, followed by a rough scope and cost range before any commitment.' },
  { q: 'What is the cost of hospital infrastructure design in Khammam?', a: 'Costs vary by project scope, from ICU fit-outs to full ward blocks. We share a detailed, itemised estimate after a free consultation.' },
  { q: 'How long will my project in Khammam take?', a: 'OT fit-outs typically take 60–90 days from design sign-off. Full ward blocks or diagnostic centres take 120–180 days, with a dedicated project manager throughout.' },
  { q: 'What is the starting price for hospital infrastructure near me?', a: 'Pricing depends on scope, finishes and site conditions. We share a detailed, itemised estimate after a free consultation — no hidden costs.' },
  { q: 'Can I get a free site assessment in Khammam?', a: 'Yes — request a free consultation below and our team will schedule a site visit to assess your facility and share a proposal.' },
];

/* ── Inline lead form (used in hero and CTA banner) ── */
function LeadForm({ compact }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState(CITY.name);
  const [whatsapp, setWhatsapp] = useState(true);
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone) return;
    const lines = [
      `Hi, I'd like a free consultation for hospital infrastructure.`,
      '',
      `Name: ${name}`,
      `Email: ${email || '(not provided)'}`,
      `Phone: ${phone}`,
      `City: ${city}`,
      `WhatsApp updates: ${whatsapp ? 'Yes' : 'No'}`,
    ];
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <div className={`cp-form-card${compact ? ' cp-form-compact' : ''}`}>
      <div className="cp-form-title">Talk to a Hospital Consultant</div>
      {sent ? (
        <div className="cp-form-sent">✓ We've opened WhatsApp with your details — just hit send.</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <input className="cp-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="cp-input" placeholder="Email ID" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="cp-input" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <label className="cp-check-row">
            <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />
            <span>Send me updates on WhatsApp</span>
          </label>
          <select className="cp-input" value={city} onChange={(e) => setCity(e.target.value)}>
            {CITIES_LIST.map((c) => <option key={c}>{c}</option>)}
          </select>
          <button type="submit" className="cp-form-submit">Get Free Quote →</button>
          <p className="cp-form-fineprint">By submitting this form, you agree to our privacy policy &amp; terms of service.</p>
        </form>
      )}
    </div>
  );
}

export default function Khammam() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const ecMapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(CITY.nearestEC.address)}&output=embed`;

  return (
    <>
    <SEO/>
    <div className="cp-page">
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        img { display: block; }

        .cp-page {
          background: #fff; color: #343F86; overflow-x: hidden;
          font-family: 'Inter', 'Montserrat', sans-serif;
          padding-top: 68px;
        }
        @media (max-width: 900px) { .cp-page { padding-top: 60px; } }
        @media (max-width: 480px) { .cp-page { padding-top: 56px; } }

        /* ── HERO ── */
        .cp-hero { background: #343F86; padding: 56px 48px; position: relative; overflow: hidden; }
        .cp-hero-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }
        .cp-hero-inner {
          position: relative; z-index: 2; max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: 1.2fr 1fr; gap: 40px; align-items: start;
        }
        .cp-hero-eyebrow {
          display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 3px;
          text-transform: uppercase; color: #7FD0DA; background: rgba(255,255,255,0.08);
          padding: 6px 14px; border-radius: 4px; margin-bottom: 18px;
        }
        .cp-hero h1 { font-size: clamp(28px,4.5vw,44px); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 14px; }
        .cp-hero p { font-size: 15px; color: #A7ACBD; line-height: 1.8; margin-bottom: 20px; max-width: 480px; }
        .cp-hero-badge {
          display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15); border-radius: 20px; padding: 8px 16px;
          font-size: 12px; color: #CDD1E0; margin-top: 8px;
        }

        /* ── FORM CARD ── */
        .cp-form-card { background: #fff; border-radius: 16px; padding: 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
        .cp-form-compact { max-width: 420px; margin: 0 auto; }
        .cp-form-title { font-size: 16px; font-weight: 700; color: #343F86; margin-bottom: 16px; }
        .cp-input {
          width: 100%; background: #F6F7FC; border: 1px solid #E1E3EE; border-radius: 8px;
          padding: 11px 14px; font-size: 13.5px; color: #343F86; font-family: 'Inter', 'Montserrat', sans-serif;
          margin-bottom: 12px; outline: none;
        }
        .cp-input:focus { border-color: #343F86; background: #fff; }
        .cp-check-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #5B6072; margin-bottom: 14px; }
        .cp-form-submit {
          width: 100%; background: #343F86; color: #7FD0DA; border: none; border-radius: 10px;
          padding: 13px 0; font-size: 13px; font-weight: 700; cursor: pointer; letter-spacing: 0.3px;
          text-transform: uppercase; font-family: 'Inter', 'Montserrat', sans-serif;
        }
        .cp-form-submit:hover { background: #262E63; }
        .cp-form-fineprint { font-size: 10.5px; color: #8A8F9C; margin-top: 10px; line-height: 1.5; text-align: center; }
        .cp-form-sent { font-size: 13.5px; color: #1a6e45; background: #e8f5ee; border-radius: 8px; padding: 16px; text-align: center; line-height: 1.6; }

        /* ── Breadcrumb ── */
        .cp-breadcrumb { font-size: 12px; color: #8A8F9C; padding: 12px 48px; max-width: 1200px; margin: 0 auto; }
        .cp-breadcrumb span { color: #343F86; font-weight: 600; }

        /* ── Shared section ── */
        .cp-section { max-width: 1200px; margin: 0 auto; padding: 48px 48px; }
        .cp-section-alt { background: #F6F7FC; }
        .cp-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: #2F8A98; margin-bottom: 10px; }
        .cp-h2-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; margin-bottom: 28px; }
        .cp-h2 { font-size: 26px; font-weight: 700; color: #343F86; margin-bottom: 6px; }
        .cp-sub { font-size: 14px; color: #5B6072; line-height: 1.7; max-width: 560px; }
        .cp-cta-pill {
          background: #343F86; color: #7FD0DA; border: none; border-radius: 8px; padding: 11px 22px;
          font-size: 12.5px; font-weight: 700; cursor: pointer; text-transform: uppercase;
          letter-spacing: 0.4px; white-space: nowrap; font-family: 'Inter', 'Montserrat', sans-serif;
        }

        /* ── Detailed image/map+info block (EC and local project) ── */
        .cp-btn-dark { background: #343F86; color: #fff; border: none; border-radius: 8px; padding: 10px 20px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: 'Inter', 'Montserrat', sans-serif; text-decoration: none; display: inline-block; }
        .cp-btn-outline { background: #fff; color: #343F86; border: 1.5px solid #343F86; border-radius: 8px; padding: 10px 20px; font-size: 12.5px; font-weight: 700; cursor: pointer; text-decoration: none; display: inline-block; font-family: 'Inter', 'Montserrat', sans-serif; }
        .cp-ec-block { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #E1E3EE; border-radius: 16px; overflow: hidden; }
        .cp-ec-visual {
          height: 100%; min-height: 280px; background: #262E63; position: relative;
          display: flex; align-items: center; justify-content: center;
        }
        .cp-ec-map-frame { width: 100%; height: 100%; min-height: 280px; border: none; display: block; }
        .cp-ec-visual-icon { font-size: 48px; opacity: 0.7; }
        .cp-ec-tag { position: absolute; top: 14px; left: 14px; background: rgba(0,0,0,0.65); color: #fff; font-size: 11px; padding: 5px 12px; border-radius: 20px; z-index: 2; pointer-events: none; }
        .cp-ec-info { padding: 28px; }
        .cp-ec-label { font-size: 10.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #7FD0DA; margin-bottom: 6px; margin-top: 14px; }
        .cp-ec-label:first-child { margin-top: 0; }
        .cp-ec-value { font-size: 13.5px; color: #262E63; line-height: 1.6; }
        .cp-ec-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 22px; }

        /* ── Process 3-card ── */
        .cp-process-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .cp-process-card { border-radius: 14px; overflow: hidden; border: 1px solid #E1E3EE; background: #fff; }
        .cp-process-visual { height: 130px; background: #262E63; display: flex; align-items: center; justify-content: center; font-size: 34px; color: #fff; }
        .cp-process-body { padding: 18px 20px 22px; }
        .cp-process-title { font-size: 15px; font-weight: 700; color: #343F86; margin-bottom: 6px; }
        .cp-process-text { font-size: 12.5px; color: #5B6072; line-height: 1.6; }

        /* ── Price estimator 3-card ── */
        .cp-estimator-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .cp-estimator-card { border: 1px solid #E1E3EE; border-radius: 14px; padding: 20px; background: #fff; display: flex; flex-direction: column; }
        .cp-estimator-icon { font-size: 28px; margin-bottom: 14px; }
        .cp-estimator-title { font-size: 14.5px; font-weight: 700; color: #343F86; margin-bottom: 6px; }
        .cp-estimator-text { font-size: 12.5px; color: #5B6072; line-height: 1.6; margin-bottom: 16px; flex: 1; }
        .cp-estimator-btn { align-self: flex-start; background: #343F86; color: #fff; border: none; border-radius: 8px; padding: 9px 18px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: 'Inter', 'Montserrat', sans-serif; }

        /* ── Reviews ── */
        .cp-reviews-header { display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap; margin-bottom: 28px; }
        .cp-rating-badge { display: flex; align-items: center; gap: 12px; background: #F6F7FC; border-radius: 14px; padding: 14px 20px; }
        .cp-rating-icon { font-size: 26px; }
        .cp-rating-num { font-size: 24px; font-weight: 700; color: #343F86; }
        .cp-rating-stars { color: #f4b400; font-size: 14px; }
        .cp-rating-sub { font-size: 10.5px; color: #8A8F9C; }
        .cp-review-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .cp-review-card { border: 1px solid #E1E3EE; border-radius: 14px; padding: 20px; }
        .cp-review-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .cp-avatar { width: 38px; height: 38px; border-radius: 50%; background: #262E63; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
        .cp-review-name { font-size: 13.5px; font-weight: 700; color: #343F86; }
        .cp-review-stars { color: #f4b400; font-size: 12px; }
        .cp-review-text { font-size: 12.5px; color: #5B6072; line-height: 1.65; }

        /* ── Why us 6-card ── */
        .cp-why-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .cp-why-card { border: 1px solid #E1E3EE; border-radius: 12px; padding: 20px; background: #fff; }
        .cp-why-icon { font-size: 22px; margin-bottom: 10px; }
        .cp-why-title { font-size: 13.5px; font-weight: 700; color: #343F86; margin-bottom: 6px; }
        .cp-why-text { font-size: 12px; color: #5B6072; line-height: 1.6; }

        /* ── Inspiration 3-card ── */
        .cp-insp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .cp-insp-card { border-radius: 14px; overflow: hidden; border: 1px solid #E1E3EE; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; }
        .cp-insp-card:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.08); }
        .cp-insp-visual { height: 140px; background: #262E63; display: flex; align-items: center; justify-content: center; font-size: 32px; }
        .cp-insp-body { padding: 16px 18px 20px; }
        .cp-insp-title { font-size: 14px; font-weight: 700; color: #343F86; margin-bottom: 4px; }
        .cp-insp-link { font-size: 12px; color: #343F86; font-weight: 600; }

        /* ── FAQ ── */
        .cp-faq-item { border: 1px solid #E1E3EE; border-radius: 10px; margin-bottom: 8px; background: #fff; overflow: hidden; }
        .cp-faq-btn { width: 100%; background: none; border: none; padding: 16px 18px; display: flex; justify-content: space-between; align-items: center; gap: 12px; text-align: left; cursor: pointer; font-size: 13.5px; font-weight: 600; color: #343F86; font-family: 'Inter', 'Montserrat', sans-serif; }
        .cp-faq-icon { font-size: 18px; color: #5B6072; flex-shrink: 0; transition: transform 0.25s; }
        .cp-faq-icon.open { transform: rotate(45deg); }
        .cp-faq-ans { padding: 0 18px 16px; font-size: 13px; color: #5B6072; line-height: 1.75; }

        /* ── CTA banner with form ── */
        .cp-cta { background: linear-gradient(135deg, #343F86 0%, #262E63 55%, #343F86 100%); padding: 56px 48px; position: relative; overflow: hidden; }
        .cp-cta-dots { position: absolute; inset: 0; opacity: 0.04; background-image: radial-gradient(circle at 50% 50%, #fff 1px, transparent 1px); background-size: 36px 36px; }
        .cp-cta-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1.2fr 1fr; gap: 40px; align-items: center; position: relative; z-index: 1; }
        .cp-cta h2 { font-size: clamp(24px,3.5vw,36px); font-weight: 700; color: #fff; line-height: 1.2; margin-bottom: 12px; }
        .cp-cta p { font-size: 14px; color: #A7ACBD; line-height: 1.7; }

        /* ── Nearby cities strip ── */
        .cp-nearby { background: #F6F7FC; border-top: 1px solid #ECEEF6; padding: 32px 48px; }
        .cp-nearby-inner { max-width: 1200px; margin: 0 auto; }
        .cp-nearby-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #7FD0DA; margin-bottom: 14px; }
        .cp-nearby-links { display: flex; flex-wrap: wrap; gap: 10px; }
        .cp-nearby-link { font-size: 12.5px; color: #3A4060; border: 1px solid #E1E3EE; border-radius: 20px; padding: 6px 14px; cursor: pointer; background: #fff; transition: all 0.2s; }
        .cp-nearby-link:hover { border-color: #343F86; color: #343F86; }

        /* ── MOBILE ── */
        @media (max-width: 900px) {
          .cp-hero-inner { grid-template-columns: 1fr; }
          .cp-ec-block { grid-template-columns: 1fr; }
          .cp-process-grid, .cp-estimator-grid, .cp-review-grid, .cp-why-grid, .cp-insp-grid { grid-template-columns: repeat(2, 1fr); }
          .cp-cta-inner { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .cp-hero { padding: 40px 20px; }
          .cp-breadcrumb { padding: 10px 20px; }
          .cp-section { padding: 32px 20px; }
          .cp-h2 { font-size: 21px; }
          .cp-process-grid, .cp-estimator-grid, .cp-review-grid, .cp-why-grid, .cp-insp-grid { grid-template-columns: 1fr; }
          .cp-ec-block { grid-template-columns: 1fr; }
          .cp-ec-visual, .cp-ec-map-frame { min-height: 200px; }
          .cp-cta { padding: 40px 20px; }
          .cp-nearby { padding: 24px 20px; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div className="cp-hero">
        <div className="cp-hero-grid" />
        <div className="cp-hero-inner">
          <div>
            <div className="cp-hero-eyebrow">Hospital Infrastructure · {CITY.name}, {CITY.state}</div>
            <h1>Hospital Infrastructure Designers in {CITY.name}</h1>
            <p>
              From OT suites to full ward blocks — PR Hospital Designers brings NABH-ready hospital design
              and build to {CITY.name} and the surrounding region.
            </p>
            <div className="cp-hero-badge">🛡️ NABH-Trusted Infrastructure Partner</div>
          </div>
          <LeadForm />
        </div>
      </div>

      {/* ── BREADCRUMB ── */}
      <div className="cp-breadcrumb">Home / Cities / <span>{CITY.name}</span></div>

      {/* ── EXPERIENCE CENTRE ── */}
      <div className="cp-section">
        <div className="cp-h2-row">
          <div>
            <div className="cp-eyebrow">Nearest to Khammam</div>
            <h2 className="cp-h2">Where you can meet us</h2>
            <p className="cp-sub">We don't have a walk-in centre in Khammam yet — here's our nearest Experience Centre, and a project we've already completed in your city.</p>
          </div>
          <button className="cp-cta-pill" onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}`, '_blank')}>Meet Your Consultant</button>
        </div>

        <div className="cp-ec-block">
          <div className="cp-ec-visual">
            <span className="cp-ec-tag">{CITY.nearestEC.city}</span>
            <iframe
              className="cp-ec-map-frame"
              src={ecMapEmbedSrc}
              title="PR Infra & Hospitals Designers — map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="cp-ec-info">
            <div className="cp-ec-label">Name</div>
            <div className="cp-ec-value">{CITY.nearestEC.name}</div>
            <div className="cp-ec-label">Address</div>
            <div className="cp-ec-value">{CITY.nearestEC.address}</div>
            <div className="cp-ec-label">Timings</div>
            <div className="cp-ec-value">{CITY.nearestEC.hours}</div>
            <div className="cp-ec-label">Appointment Types</div>
            <div className="cp-ec-value">{CITY.nearestEC.appt1}<br />{CITY.nearestEC.appt2}</div>
            <div className="cp-ec-label">Contact Number</div>
            <div className="cp-ec-value">{CITY.nearestEC.phone}</div>
            <div className="cp-ec-actions">
              <a className="cp-btn-outline" href={CITY.nearestEC.mapsUrl} target="_blank" rel="noopener noreferrer">🗺️ Directions</a>
              <a className="cp-btn-dark" href={`tel:${CITY.nearestEC.phone.replace(/\s/g, '')}`}>📞 Call</a>
            </div>
          </div>
        </div>

        {/* Local completed project — same detailed block style as the EC above */}
        <div className="cp-ec-block" style={{ marginTop: 20 }}>
          <div className="cp-ec-visual">
            <span className="cp-ec-tag">{CITY.name}</span>
            <span className="cp-ec-visual-icon">🏥</span>
          </div>
          <div className="cp-ec-info">
            <div className="cp-ec-label">Name</div>
            <div className="cp-ec-value">{CITY.localProject.name}</div>
            <div className="cp-ec-label">Location</div>
            <div className="cp-ec-value">{CITY.localProject.location}, {CITY.state}</div>
            <div className="cp-ec-label">Project Type</div>
            <div className="cp-ec-value">{CITY.localProject.type}</div>
            <div className="cp-ec-label">Status</div>
            <div className="cp-ec-value">✓ Completed</div>
            <div className="cp-ec-actions">
              <a className="cp-btn-outline" href={CITY.localProject.mapsUrl} target="_blank" rel="noopener noreferrer">🗺️ View on Map</a>
              <button className="cp-btn-dark" onClick={() => navigate('/projects')}>All Projects</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── PROCESS ── */}
      <div className="cp-section cp-section-alt" style={{ maxWidth: '100%' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="cp-h2-row">
            <div>
              <div className="cp-eyebrow">How It Works</div>
              <h2 className="cp-h2">Hospital infrastructure becoming a hassle?</h2>
              <p className="cp-sub">Our team makes your hospital build journey smooth and hassle-free.</p>
            </div>
          </div>
          <div className="cp-process-grid">
            {PROCESS_STEPS.map((s) => (
              <div key={s.title} className="cp-process-card">
                <div className="cp-process-visual">{s.icon}</div>
                <div className="cp-process-body">
                  <div className="cp-process-title">{s.title}</div>
                  <div className="cp-process-text">{s.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRICE ESTIMATOR ── */}
      <div className="cp-section">
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div className="cp-eyebrow" style={{ justifyContent: 'center' }}>Estimate Instantly</div>
          <h2 className="cp-h2">Hospital Infrastructure Price Estimator</h2>
          <p className="cp-sub" style={{ margin: '0 auto' }}>Calculate the approximate cost of your hospital project.</p>
        </div>
        <div className="cp-estimator-grid">
          {ESTIMATORS.map((e) => (
            <div key={e.title} className="cp-estimator-card">
              <div className="cp-estimator-icon">{e.icon}</div>
              <div className="cp-estimator-title">{e.title}</div>
              <div className="cp-estimator-text">{e.text}</div>
              <button className="cp-estimator-btn" onClick={() => navigate('/cost-calculator')}>Calculate →</button>
            </div>
          ))}
        </div>
      </div>

      {/* ── REVIEWS ── */}
      <div className="cp-section cp-section-alt" style={{ maxWidth: '100%' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="cp-reviews-header">
            <div>
              <div className="cp-eyebrow">Customer Feedback</div>
              <h2 className="cp-h2">PR Hospital Designers Hospital Infrastructure Reviews</h2>
            </div>
            <div className="cp-rating-badge">
              <span className="cp-rating-icon">G</span>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className="cp-rating-num">4.8</span>
                  <span className="cp-rating-stars">★★★★★</span>
                </div>
                <div className="cp-rating-sub">based on Google Reviews</div>
              </div>
            </div>
          </div>
          <div className="cp-review-grid">
            {REVIEWS.map((r) => (
              <div key={r.name} className="cp-review-card">
                <div className="cp-review-top">
                  <div className="cp-avatar">{r.initials}</div>
                  <div>
                    <div className="cp-review-name">{r.name}</div>
                    <div className="cp-review-stars">★★★★★</div>
                  </div>
                </div>
                <div className="cp-review-text">{r.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WHY US ── */}
      <div className="cp-section">
        <div className="cp-eyebrow">Why PR Hospital Designers</div>
        <h2 className="cp-h2">Reasons to choose us</h2>
        <p className="cp-sub" style={{ marginBottom: 28 }}>Deep healthcare domain knowledge combined with construction expertise.</p>
        <div className="cp-why-grid">
          {WHY_US.map((w) => (
            <div key={w.title} className="cp-why-card">
              <div className="cp-why-icon">{w.icon}</div>
              <div className="cp-why-title">{w.title}</div>
              <div className="cp-why-text">{w.text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── INSPIRATION / RECENT PROJECTS ── */}
      <div className="cp-section cp-section-alt" style={{ maxWidth: '100%' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="cp-h2-row">
            <div>
              <div className="cp-eyebrow">Need Some Inspiration?</div>
              <h2 className="cp-h2">Explore our design ideas</h2>
              <p className="cp-sub">From OT suites to patient wards — see what we build.</p>
            </div>
            <button className="cp-cta-pill" onClick={() => navigate('/projects')}>View All</button>
          </div>
          <div className="cp-insp-grid">
            {INSPIRATION.map((i) => (
              <div key={i.title} className="cp-insp-card" onClick={() => navigate(i.path)}>
                <div className="cp-insp-visual">{i.icon}</div>
                <div className="cp-insp-body">
                  <div className="cp-insp-title">{i.title}</div>
                  <div className="cp-insp-link">Explore →</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="cp-section">
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div className="cp-eyebrow">Common Questions</div>
          <h2 className="cp-h2">Hospital Infrastructure in {CITY.name} — FAQs</h2>
          <div style={{ marginTop: 20 }}>
            {FAQS.map((faq, i) => (
              <div key={i} className="cp-faq-item">
                <button className="cp-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{i + 1}. {faq.q}</span>
                  <span className={`cp-faq-icon${openFaq === i ? ' open' : ''}`}>+</span>
                </button>
                {openFaq === i && <div className="cp-faq-ans">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA + FORM ── */}
      <div className="cp-cta">
        <div className="cp-cta-dots" />
        <div className="cp-cta-inner">
          <div>
            <h2>Your dream hospital<br />is just a click away</h2>
            <p>Turn your facility vision into reality. Consult our hospital infrastructure experts.</p>
          </div>
          <LeadForm compact />
        </div>
      </div>

      {/* ── NEARBY CITIES ── */}
      <div className="cp-nearby">
        <div className="cp-nearby-inner">
          <div className="cp-nearby-title">We also serve</div>
          <div className="cp-nearby-links">
            {CITIES_LIST.filter((c) => c !== CITY.name).map((c) => (
              <span key={c} className="cp-nearby-link" onClick={() => navigate(CITY_PATHS[c])}>
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
